import { I18n, Translate } from 'common'
import { observer } from 'mobx-react-lite'
import { ReactElement, useEffect, useRef } from 'react'
import { useSelector } from 'react-redux'
import { ImageBypartAPIResponse } from 'src/api/searchResults/interfaces'
import { Vehicle } from 'src/store/models/Vehicles'
import { useUiStateStore } from 'src/store/uiState/UiStateStore'
import { UserAttributeKey } from 'src/store/user/interfaces'
import { useUserStore } from 'src/store/user/UserStore'
import { defaultTheme } from 'src/theme'
import { breakpoints } from 'src/theme/breakpoints'
import { Checkbox, Thumbnail } from 'src/ui-components'
import { RootState, useAppDispatch } from 'store'
import styled from 'styled-components'
import { ShipToDropdown } from './ShipToDropdown'
import {
  getVehicleOrderFormData,
  shouldShowVehicleOrderFormError,
} from '../store/cartSelectors'
import { cartSlice } from '../store/cartStore'
import { SellerWithLocations } from '../store/ISellerCart'
import { setCopyToAllCarts } from '../store/uiStore'
import { OrderFormTextInput } from './OrderFormTextInput'
import { SellerLocations } from './SellerLocations'
import { formatVehicleDisplay } from 'src/helpers/utils'
import { isMiscellaneousVehicle } from 'src/features/search/Results/utils/miscellaneousVehicle'

interface NewSellerCartOrderFormProps {
  seller: SellerWithLocations
  partImages: ImageBypartAPIResponse[]
  currentVehicle: Vehicle
  IsSamePoNumber: boolean
}

export const MainCartOrderForm = observer(
  ({
    seller,
    partImages,
    currentVehicle,
    IsSamePoNumber,
  }: NewSellerCartOrderFormProps): ReactElement => {
    const userStore = useUserStore()
    const { isMultisellerUser, showYourCost, isDisplayMultiSellerLogos } =
      useUiStateStore()
    const displayExclTax = userStore?.preferences?.displayExclTax === 'true'

    const noteRequired = userStore?.preferences?.cart_noteRequired === 'true'
    const noteMaxSize = userStore?.preferences?.cart_noteMaxSize

    const displayShipToAddressInCartPage =
      userStore.getUserAttribute(
        UserAttributeKey.displayShipToAddressInCartPage
      ) === 'true'

    const poRequired = userStore?.preferences?.cart_poRequired === 'true'
    const poMaxSize = userStore?.preferences?.cart_poMaxSize
    const displayMultiSellerLogos =
      !seller?.coverageKey || isDisplayMultiSellerLogos()

    const showError: boolean = useSelector((state: RootState) => {
      return shouldShowVehicleOrderFormError(state, currentVehicle)
    })

    const dispatch = useAppDispatch()

    const getVehicleString = (vehicle: Vehicle): string => {
      if (isMiscellaneousVehicle(vehicle) || !vehicle.year) {
        return ''
      }
      return formatVehicleDisplay(vehicle).substring(0, Number(noteMaxSize))
    }

    const cartNoteHasVehicle: boolean =
      userStore?.preferences[UserAttributeKey.cartNoteHasVehicle] === 'true'

    const { poNumber, noteToStore, personalNote, customerName } = useSelector(
      (state: RootState) => {
        return getVehicleOrderFormData(state, currentVehicle)
      }
    )

    const { isCopyToAllCarts, commonOrderFormData } = useSelector(
      (state: RootState) => {
        return {
          isCopyToAllCarts: state.ui.copyToAllCarts,
          commonOrderFormData: state.cart.commonOrderFormData,
        }
      }
    )

    // Sync vehicle-level orderFormData into seller-level fields when seller fields are empty.
    // This is required when carts are added from quotes — the quote flow sets poNumber/noteToStore
    // on vehicle-level orderFormData, but not on seller-level fields. Without this sync,
    // the form inputs appear pre-filled because they use a fallback chain
    // (e.g. value={seller.poNumber ? seller.poNumber : poNumber}), where `poNumber` comes from
    // vehicle-level orderFormData via the getVehicleOrderFormData selector. However, the order
    // validation and request (makeOrderLocations in Utils.ts) only reads seller-level fields,
    // so the order would fail with "Order form is incomplete" despite the user seeing filled values.
    useEffect(() => {
      if (
        (seller.noteToStore === null || seller.noteToStore === undefined) &&
        noteToStore
      ) {
        dispatch(
          cartSlice.actions.setFieldOnSeller({
            field: 'noteToStore',
            vehicle: currentVehicle,
            seller,
            value: noteToStore,
          })
        )
      }

      if (
        (seller.poNumber === null || seller.poNumber === undefined) &&
        poNumber
      ) {
        dispatch(
          cartSlice.actions.setFieldOnSeller({
            field: 'poNumber',
            vehicle: currentVehicle,
            seller,
            value: poNumber,
          })
        )
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const hasInitializedNoteToStore = useRef(false)

    // Initialize seller's noteToStore with default vehicle string if empty and cartNoteHasVehicle is enabled
    useEffect(() => {
      if (!cartNoteHasVehicle) {
        hasInitializedNoteToStore.current = false
        return
      }
      if (hasInitializedNoteToStore.current) return

      const value = getVehicleString(currentVehicle) || ''
      if (!value) return

      // Check for both null and empty string (only on initial load)
      if (!seller.noteToStore || seller.noteToStore.trim() === '') {
        dispatch(
          cartSlice.actions.setFieldOnSeller({
            field: 'noteToStore',
            vehicle: currentVehicle,
            seller,
            value,
          })
        )
        hasInitializedNoteToStore.current = true
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cartNoteHasVehicle, currentVehicle, getVehicleString, dispatch])

    const hasSyncedFromCommon = useRef(false)

    useEffect(() => {
      // RESET when checkbox is turned OFF
      if (!isCopyToAllCarts) {
        hasSyncedFromCommon.current = false
        return
      }

      // Prevent repeated syncing
      if (hasSyncedFromCommon.current) return

      if (commonOrderFormData.poNumber) {
        dispatch(
          cartSlice.actions.setFieldOnSeller({
            field: 'poNumber',
            vehicle: currentVehicle,
            seller,
            value: commonOrderFormData.poNumber,
          })
        )
      }

      if (commonOrderFormData.noteToStore) {
        dispatch(
          cartSlice.actions.setFieldOnSeller({
            field: 'noteToStore',
            vehicle: currentVehicle,
            seller,
            value: commonOrderFormData.noteToStore,
          })
        )
      }

      hasSyncedFromCommon.current = true
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
      isCopyToAllCarts,
      commonOrderFormData.poNumber,
      commonOrderFormData.noteToStore,
      currentVehicle,
      dispatch,
    ])

    return (
      <SellerInfoWrapper key={seller.coverageKey}>
        {isMultisellerUser() && (
          <SellerHeader>
            <SellerTitleWrapper>
              {displayMultiSellerLogos && (
                <Thumbnail
                  urlOrBase64={
                    seller.coverageId === 0
                      ? userStore.userLogo
                      : seller.logoUrl
                  }
                  width={100}
                  height={36}
                  alt={`${seller.sellerName} logo`}
                  placeholder
                />
              )}
              <StyledItemsTitle
                style={{
                  paddingTop: '5px',
                  paddingLeft: 0,
                  paddingBottom: 0,
                  marginLeft: displayMultiSellerLogos ? '20px' : '0px',
                }}
              >
                <I18n
                  name="sellerOrder"
                  textReplacement={[seller.sellerName]}
                />
              </StyledItemsTitle>
            </SellerTitleWrapper>
            {/* <LocalLink onClick={handleAdditionalOptionsClick}>
              <I18n
                name="additionalOrderOptions"
                style={{ fontSize: '14px' }}
              />
              {showAdditionalOptions ? <ArrowDropUp /> : <ArrowDropDown />}
            </LocalLink> */}
          </SellerHeader>
        )}

        <FormContainer>
          <FormFieldsRow>
            <OrderFormTextInput
              width="228px"
              label="poNumber"
              onChange={(value) =>
                dispatch(
                  cartSlice.actions.setFieldOnSeller({
                    field: 'poNumber',
                    vehicle: currentVehicle,
                    seller,
                    value,
                  })
                )
              }
              value={seller.poNumber ?? ''}
              disabled={IsSamePoNumber}
              noMargin
              required={poRequired}
              showMaxSize
              maxSize={Number(poMaxSize)}
              showError={showError}
              error={showError && poRequired && !seller.poNumber}
            />
            <OrderFormTextInput
              width="228px"
              label="customerName"
              onChange={(value) =>
                dispatch(
                  cartSlice.actions.setFieldOnVehicleCart({
                    field: 'customerName',
                    vehicle: currentVehicle,
                    value,
                  })
                )
              }
              value={customerName}
              showSubLabel
              noMargin
            />
            <OrderFormTextInput
              width="450px"
              label="noteToStore"
              onChange={(value) =>
                dispatch(
                  cartSlice.actions.setFieldOnSeller({
                    field: 'noteToStore',
                    vehicle: currentVehicle,
                    seller,
                    value,
                  })
                )
              }
              value={seller.noteToStore ?? ''}
              showMaxSize
              maxSize={Number(noteMaxSize)}
              required={noteRequired}
              showError={showError}
              disabled={false}
              error={showError && noteRequired && !seller.noteToStore}
              noMargin
            />
          </FormFieldsRow>

          <Checkbox
            value={1}
            checkOutlinedIconStyle={{
              fontSize: 12,
              stroke: defaultTheme.colors.border,
              strokeWidth: '1',
            }}
            labelStyle={{
              fontFamily: 'Roboto',
              fontStyle: 'normal',
              fontWeight: 400,
              fontSize: 11,
              color: defaultTheme.colors.almostBlack,
            }}
            label={Translate('copyToAllCarts')}
            active={isCopyToAllCarts}
            onClick={({ isChecked }) => {
              dispatch(setCopyToAllCarts(isChecked))

              if (isChecked) {
                dispatch(
                  cartSlice.actions.setCommonOrderFormData({
                    poNumber: seller.poNumber ? seller.poNumber : poNumber,
                    noteToStore: seller.noteToStore,
                  })
                )
              } else {
                dispatch(
                  cartSlice.actions.setCommonOrderFormData({
                    poNumber: '',
                    noteToStore: '',
                  })
                )
              }
            }}
          />
          <FormFieldsRow $marginTop="15px">
            <OrderFormTextInput
              width="395px"
              label="personalNote"
              onChange={(value) =>
                dispatch(
                  cartSlice.actions.setFieldOnVehicleCart({
                    field: 'personalNote',
                    vehicle: currentVehicle,
                    value,
                  })
                )
              }
              value={personalNote}
              showSubLabel
              noMargin
            />
            {displayShipToAddressInCartPage && seller.coverageKey === '' && (
              <ShipToDropdown
                vehicle={currentVehicle}
                onSelect={(address: string) =>
                  dispatch(
                    cartSlice.actions.setFieldOnVehicleCart({
                      field: 'shipToAddress',
                      vehicle: currentVehicle,
                      value: address,
                    })
                  )
                }
              />
            )}
          </FormFieldsRow>
        </FormContainer>

        <StyledItemsLaborSpecsTitleWrapper>
          <StyledItemsTitle>
            <I18n name="products" />
          </StyledItemsTitle>
        </StyledItemsLaborSpecsTitleWrapper>

        <CartTableScroll>
          <CartTable>
            <HeaderRow>
              <tr>
                <ColHeader>
                  <I18n name="item" />
                </ColHeader>
                <ColHeader>&nbsp;</ColHeader>
                {showYourCost() && (
                  <ColHeader>
                    <I18n name="yourCost" />
                  </ColHeader>
                )}
                <ColHeader>
                  <I18n name="listPrice" />
                </ColHeader>
                <ColHeader>
                  <I18n name="availability" />
                </ColHeader>
                <ColHeader>
                  <I18n name="quantity" />
                </ColHeader>
                <ColHeader>
                  <I18n name="total" />
                  {displayExclTax && (
                    <ExclTaxText>
                      <I18n name="displayExclTax" />
                    </ExclTaxText>
                  )}
                </ColHeader>
                <ColHeader>&nbsp;</ColHeader>
              </tr>
            </HeaderRow>
            <tbody>
              <SellerLocations
                partImages={partImages}
                vehicle={currentVehicle}
                sellerDetails={seller}
              />
            </tbody>
          </CartTable>
        </CartTableScroll>
      </SellerInfoWrapper>
    )
  }
)

const SellerInfoWrapper = styled.div`
  border-bottom: 1px solid ${defaultTheme.colors.border};
  margin-top: 20px;
`

const SellerTitleWrapper = styled.div`
  display: flex;
  flex-direction: row;
`

const SellerHeader = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  margin-left: 15px;
`

export const CartSubtitle = styled.h4`
  font-family: 'Oswald', serif;
  font-style: normal;
  font-weight: 700;
  font-size: 20px;
  line-height: 30px;
  color: ${defaultTheme.colors.text};
  mix-blend-mode: normal;
  margin: 0;
  text-align: left;
`

export const StyledItemsTitle = styled(CartSubtitle)`
  padding: 20px 14px;
  @media (max-width: ${breakpoints.mobileLarge}px) {
    padding: 22px 14px;
  }
`

export const StyledItemsLaborSpecsTitleWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  background-color: ${defaultTheme.colors.white};
  color: ${defaultTheme.colors.primaryText};
  @media (max-width: ${breakpoints.mobileLarge}px) {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
  }
`

export const CartTable = styled.table`
  width: 100%;

  @media (max-width: ${breakpoints.mobileLarge}px) {
    display: block;

    tbody {
      display: block;
      width: 100%;
    }
  }
`

export const CartTableScroll = styled.div`
  width: 100%;
  max-width: 100%;
  overflow-x: auto;

  @media (max-width: ${breakpoints.mobileLarge}px) {
    overflow-x: visible;
  }
`

export const HeaderRow = styled.thead`
  background-color: ${defaultTheme.colors.white};
  color: ${defaultTheme.colors.primaryText};
  border-bottom: 1px solid ${defaultTheme.colors.border};
  @media (max-width: ${breakpoints.mobileLarge}px) {
    display: none;
  }
`

export const ColHeader = styled.td`
  background-color: ${defaultTheme.colors.white};
  font-weight: bold;
  font-size: 18px;
  line-height: 24px;
  padding: 20px 14px 8px 14px;
  text-align: left;
  width: 14%;
  vertical-align: top;
`

const ExclTaxText = styled.div`
  font-style: italic;
  font-weight: 400;
  font-size: 11px;
  color: ${defaultTheme.colors.columnBorder};
`

const FormContainer = styled.div`
  width: 100%;
  max-width: 100%;
  background-color: ${defaultTheme.colors.componentsBackground};
  overflow-x: hidden;
  display: flex;
  flex-direction: column;
  @media (max-width: ${breakpoints.mobileLarge}px) {
    padding: 5px 23px;
  }
  margin-top: 0px;
  margin-left: 10px;

  @media (max-width: ${breakpoints.tablet}px) {
    margin-left: 0;
  }
`

const FormFieldsRow = styled.div<{ $marginTop?: string }>`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 12px;
  width: 100%;
  margin-top: ${(p) => p.$marginTop ?? '0'};

  @media (max-width: ${breakpoints.tablet}px) {
    flex-direction: column;
  }
`
